import { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import Draggable from 'react-draggable'
import Hammer from 'react-hammerjs'
import { ItemDataTypes } from 'store/types'

type ItemSize = { height?: string; width?: string }
type ItemSizeAsPx = { height?: number; width?: number }
type ParentSizeAsPx = { height?: number; width?: number }

type PostItemWrapperProps = {
  children: React.ReactNode
  data: ItemDataTypes
  id: string
  selected: boolean
  zIndex: number
  onMouseDown: () => void
  onItemDataChange: (item: ItemDataTypes) => void
  parentSize: ParentSizeAsPx
}

const calculateBorder = (element: HTMLDivElement | null, borderWidth: string) => {
  if (!element) return 0
  const size = element.clientWidth > element.clientHeight ? element.clientWidth : element.clientHeight
  return ((size / 2) * +borderWidth.replace('%', '')) / 100
}

const PostItemWrapper = forwardRef<HTMLDivElement, PostItemWrapperProps>(
  ({ children, data, id, selected, zIndex, onMouseDown, onItemDataChange, parentSize }, ref) => {
    const itemRef = useRef<HTMLDivElement>(null)

    useImperativeHandle(ref, () => itemRef.current as HTMLDivElement, [ref])

    const [pinchInitialState, setPinchInitialState] = useState<ItemSize>()

    return (
      <div
        // On mobile we must have onTouchStart instead of onMouseDown. It's done in this way to support mobile and touchscreen notebooks.
        {...(typeof window.ontouchstart === 'undefined' ? { onMouseDown } : { onTouchStart: onMouseDown })}>
        <Hammer
          options={{
            recognizers: {
              pinch: { enable: true, direction: 30 }
            }
          }}
          onPinchStart={() => setPinchInitialState({ height: data.height, width: data.width })}
          onPinchEnd={() => setPinchInitialState(undefined)}
          onPinch={e => {
            if (!pinchInitialState?.height || !pinchInitialState?.width) return
            const oldHeight = +pinchInitialState.height.replace('%', '')
            const oldWidth = +pinchInitialState.width.replace('%', '')

            const deltaYPercentage = (100 * e.deltaY * 2) / (parentSize.height || 1)
            const deltaXPercentage = (100 * e.deltaX * 2) / (parentSize.width || 1)

            onItemDataChange({
              ...data,
              height: `${oldHeight + deltaYPercentage}%`,
              width: `${oldWidth + deltaXPercentage}%`
            })
          }}>
          <div>
            <Draggable
              position={{ x: data.x, y: data.y }}
              scale={1}
              handle='.draggable-handle'
              onDrag={(e, dragData) => {
                if (e.type === 'touchmove' && (e as TouchEvent).touches.length === 2) return
                onItemDataChange({
                  ...data,
                  x: dragData.x,
                  y: dragData.y,
                  lastParentWidth: parentSize.width || 0,
                  lastParentHeight: parentSize.height || 0
                })
              }}
              onStop={(e, dragData) => {
                if (e.type === 'touchmove' && (e as TouchEvent).touches.length === 2) return
                onItemDataChange({
                  ...data,
                  x: dragData.x,
                  y: dragData.y,
                  lastParentWidth: parentSize.width || 0,
                  lastParentHeight: parentSize.height || 0
                })
              }}>
              <div
                data-id={id}
                ref={itemRef}
                className={`draggable-stop absolute border-solid resize overflow-hidden [&::-webkit-resizer]:bg-custom-resizer ${selected ? 'outline-dashed outline-2 outline-black' : ''}`}
                style={{
                  borderWidth: calculateBorder(itemRef.current, data.baseProps.borderWidth),
                  borderColor: data.baseProps.borderColor,
                  borderRadius: data.baseProps.borderRadius,
                  height: data.height,
                  width: data.width,
                  zIndex
                }}>
                {children}
                <div
                  className={`draggable-handle cursor-move absolute h-full right-0 left-0 ${
                    data.y > 10 ? 'top-0' : 'bottom-0'
                  }`}
                />
              </div>
            </Draggable>
          </div>
        </Hammer>
      </div>
    )
  }
)
PostItemWrapper.displayName = 'PostItemWrapper'

export default PostItemWrapper

// PostItemWrapperPreview

type PostItemWrapperPreviewProps = {
  children: React.ReactNode
  data: ItemDataTypes
  parentSize: {
    height?: number
    width?: number
  }
  itemSizeAsPx: ItemSizeAsPx
  previewSize: ParentSizeAsPx
}

const PostItemWrapperPreview = ({
  children,
  data,
  parentSize,
  itemSizeAsPx: textSizeAsPx,
  previewSize
}: PostItemWrapperPreviewProps) => {
  const ref = useRef<HTMLDivElement>(null)
  return (
    <Draggable
      position={{
        x: (data.x * (previewSize.width || 1)) / (parentSize.width || 1),
        y: (data.y * (previewSize.height || 1)) / (parentSize.height || 1)
      }}
      disabled>
      <div
        ref={ref}
        className='absolute border-solid overflow-hidden'
        style={{
          borderWidth: calculateBorder(ref.current, data.baseProps.borderWidth),
          borderColor: data.baseProps.borderColor,
          borderRadius: data.baseProps.borderRadius,
          ...(textSizeAsPx.height && textSizeAsPx.width && parentSize.height && parentSize.width
            ? {
                height: `${(100 * textSizeAsPx.height) / parentSize.height}%`,
                width: `${(100 * textSizeAsPx.width) / parentSize.width}%`
              }
            : {}),
          zIndex: data.zIndex
        }}>
        {children}
      </div>
    </Draggable>
  )
}

export { PostItemWrapperPreview }
